import React, { useState, useEffect } from 'react';
import { ErrandStop } from '../contexts/ErrandContext';

// Helper function to calculate distance between two coordinates using Haversine formula
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in kilometers
};

interface PricingCalculatorProps {
  errandType: {
    id: string;
    name: string;
    base_price: number;
    price_per_km: number;
  } | null;
  demandData: {
    pricing_multiplier: number;
    demand_level: string;
  } | null;
  // For single stop
  pickupCoords?: { lat: number; lng: number };
  dropoffCoords?: { lat: number; lng: number };
  // For multi-stop - accepts both full ErrandStop and creation types
  stops?: (ErrandStop | Omit<ErrandStop, 'id' | 'errand_id' | 'status' | 'created_at' | 'updated_at'>)[];
  className?: string;
}

interface PricingBreakdown {
  basePrice: number;
  stopCosts: {
    pickups: number;
    dropoffs: number;
    intermediates: number;
  };
  distanceCost: number;
  demandMultiplier: number;
  subtotal: number;
  serviceFee: number;
  totalCost: number;
  driverEarnings: number;
  totalDistance: number;
  stopsCount?: number;
}

export const PricingCalculator: React.FC<PricingCalculatorProps> = ({
  errandType,
  demandData,
  pickupCoords,
  dropoffCoords,
  stops,
  className = ''
}) => {
  const [pricing, setPricing] = useState<PricingBreakdown | null>(null);

  useEffect(() => {
    if (!errandType || !demandData) {
      setPricing(null);
      return;
    }

    let totalDistance = 0;
    let stopsCount = 0;

    if (stops && stops.length > 0) {
      // Multi-stop calculation matching server-side logic
      stopsCount = stops.length;

      // Calculate total distance by summing distances between consecutive stops
      for (let i = 0; i < stops.length - 1; i++) {
        const stop1 = stops[i];
        const stop2 = stops[i + 1];

        if (stop1.latitude && stop1.longitude && stop2.latitude && stop2.longitude) {
          const distance = calculateDistance(
            stop1.latitude, stop1.longitude,
            stop2.latitude, stop2.longitude
          );
          totalDistance += distance;
        }
      }

    } else if (pickupCoords && dropoffCoords) {
      // Single stop calculation
      totalDistance = calculateDistance(
        pickupCoords.lat,
        pickupCoords.lng,
        dropoffCoords.lat,
        dropoffCoords.lng
      );
    } else {
      // Default fallback
      totalDistance = 3; // Assume in-town distance
    }

    // Enhanced pricing logic for multi-stop errands (matching server-side)
    // Use P100 as base price for custom errands as requested
    const basePrice = 100 * demandData.pricing_multiplier; // Apply demand multiplier to base price
    const pricePerKm = errandType.price_per_km;
    const pickupDiscountRate = 0.5; // Subsequent pickups are 50% of base price

    let stopCost = 0;
    let pickupsCost = 0;
    let dropoffsCost = 0;
    let intermediatesCost = 0;

    if (stops && stops.length > 0) {
      // Multi-stop pricing logic
      const pickups = stops.filter(stop => stop.stop_type === 'pickup');
      const dropoffs = stops.filter(stop => stop.stop_type === 'dropoff');
      const intermediates = stops.filter(stop => stop.stop_type === 'intermediate');

      // Calculate stop-based pricing
      if (pickups.length > 1) {
        // First pickup gets full base price, subsequent pickups get 50%
        pickupsCost = basePrice + (pickups.length - 1) * basePrice * pickupDiscountRate;
        stopCost += pickupsCost;
      } else if (pickups.length === 1) {
        // Single pickup gets base price
        pickupsCost = basePrice;
        stopCost += basePrice;
      }

      // Add cost for drop-offs (full base price each)
      dropoffsCost = dropoffs.length * basePrice;
      stopCost += dropoffsCost;

      // Add cost for intermediate stops (reduced rate)
      intermediatesCost = intermediates.length * basePrice * 0.3;
      stopCost += intermediatesCost;
    } else {
      // Single stop pricing
      pickupsCost = basePrice;
      stopCost = basePrice;
    }

    // Calculate distance cost (only for distances > 5km)
    const isOutOfTown = totalDistance > 5.0;
    const distanceCost = isOutOfTown ? (totalDistance - 5.0) * pricePerKm : 0;

    // Calculate subtotal
    const subtotal = stopCost + distanceCost;

    // Calculate service fee (15%) - this will be deducted from driver after customer pays full amount
    const serviceFee = subtotal * 0.15;

    // Total cost customer pays (full amount to driver)
    const totalCost = subtotal;

    // Driver receives full amount initially, then pays platform fee
    const driverEarnings = subtotal;

    // Platform fee driver must pay to admin
    const platformFee = serviceFee;

    setPricing({
      basePrice: errandType.base_price,
      stopCosts: {
        pickups: pickupsCost,
        dropoffs: dropoffsCost,
        intermediates: intermediatesCost
      },
      distanceCost,
      demandMultiplier: demandData.pricing_multiplier,
      subtotal,
      serviceFee,
      totalCost,
      driverEarnings,
      totalDistance,
      stopsCount
    });
  }, [errandType, demandData, pickupCoords, dropoffCoords, stops]);

  if (!pricing) {
    return null;
  }

  return (
    <div className={`bg-green-50 border border-green-200 rounded-lg p-4 ${className}`}>
      <h3 className="text-lg font-semibold text-green-800 mb-3">
        Estimated Cost
        {pricing.stopsCount && pricing.stopsCount > 1 && (
          <span className="text-sm font-normal ml-2">
            ({pricing.stopsCount} stops)
          </span>
        )}
      </h3>

      <div className="space-y-2">
        {/* Stop-based pricing breakdown for multi-stop */}
        {pricing.stopsCount && pricing.stopsCount > 1 ? (
          <>
            {/* Pickups cost */}
            {pricing.stopCosts.pickups > 0 && (
              <div className="flex justify-between text-sm">
                <span>Pickup Stops:</span>
                <span>P{pricing.stopCosts.pickups.toFixed(2)}</span>
              </div>
            )}

            {/* Dropoffs cost */}
            {pricing.stopCosts.dropoffs > 0 && (
              <div className="flex justify-between text-sm">
                <span>Dropoff Stops:</span>
                <span>P{pricing.stopCosts.dropoffs.toFixed(2)}</span>
              </div>
            )}

            {/* Intermediates cost */}
            {pricing.stopCosts.intermediates > 0 && (
              <div className="flex justify-between text-sm">
                <span>Intermediate Stops:</span>
                <span>P{pricing.stopCosts.intermediates.toFixed(2)}</span>
              </div>
            )}
          </>
        ) : (
          /* Base Price for single stop */
          <div className="flex justify-between text-sm">
            <span>Base Price:</span>
            <span>P{pricing.basePrice.toFixed(2)}</span>
          </div>
        )}

        {/* Distance Cost */}
        {pricing.distanceCost > 0 && (
          <div className="flex justify-between text-sm">
            <span>Distance Cost ({pricing.totalDistance.toFixed(1)}km):</span>
            <span>P{pricing.distanceCost.toFixed(2)}</span>
          </div>
        )}

        {/* Demand Multiplier */}
        {pricing.demandMultiplier > 1 && (
          <div className="flex justify-between text-sm">
            <span>Demand Multiplier ({demandData?.demand_level}):</span>
            <span>{pricing.demandMultiplier.toFixed(1)}x</span>
          </div>
        )}

        {/* Multi-stop info note */}
        {pricing.stopsCount && pricing.stopsCount > 1 && (
          <div className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
            💰 Pricing: First pickup full price, additional pickups 50% off, dropoffs full price, intermediates 70% off
          </div>
        )}

        {/* Totals */}
        <div className="border-t border-green-200 pt-2 mt-2">
          <div className="flex justify-between text-sm">
            <span>Subtotal:</span>
            <span>P{pricing.subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-semibold text-green-800 text-lg border-t border-green-300 pt-2 mt-2">
            <span>You Pay to Driver:</span>
            <span>P{pricing.totalCost.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm text-blue-600 mt-1 bg-blue-50 p-2 rounded">
            <span>Driver pays to Platform (15%):</span>
            <span>P{pricing.serviceFee.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm text-green-700 mt-1">
            <span>Driver's Net Earnings:</span>
            <span>P{pricing.driverEarnings.toFixed(2)}</span>
          </div>
        </div>

        {/* Payment Flow Explanation */}
        <div className="text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded mt-3 border border-blue-200">
          <div className="font-medium mb-1">💳 Payment Flow:</div>
          <div className="space-y-1">
            <div>1. You pay the full amount to the driver</div>
            <div>2. Driver completes the errand</div>
            <div>3. Driver pays platform fee to admin</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingCalculator;
